import { useMutation } from "@apollo/client/react";
import { ADD_ATTACHMENT_SHIPMENT_ITEMS } from "../constants/graphql/mutations";

/**
 * Custom hook to handle image uploads with automatic shipment item attachment syncing.
 * 
 * When images are uploaded for an asset that has shipment items, this hook automatically
 * creates corresponding shipment item attachments with pair_id linking to the images.
 * 
 * This ensures that:
 * 1. Images uploaded in capture-inventory are linked to shipment items
 * 2. Images uploaded in asset details modal are linked if the asset was received
 * 3. Attachment syncing happens transparently without breaking existing functionality
 * 
 * @param {Object} asset - The asset object (must include shipmentItems and attachments)
 * @param {Function} bulkCreateImages - The mutation function to create images
 * @returns {Function} handleBulkCreateImages - Wrapped function that creates images and syncs attachments
 */
export default function useImageUploadWithAttachments(asset, bulkCreateImages) {
  const [addAttachmentShipmentItems] = useMutation(ADD_ATTACHMENT_SHIPMENT_ITEMS);

  /**
   * Wrapped bulk create images handler that automatically syncs shipment item attachments.
   * 
   * @param {Object} params
   * @param {Object} params.variables - Variables to pass to bulkCreateImages mutation
   * @param {Function} params.onSuccess - Success callback (receives mutation result)
   * @param {Function} params.onFailure - Failure callback (receives errors)
   * @param {boolean} params.disableNotification - Pass through to bulkCreateImages
   */
  const handleBulkCreateImages = async ({ variables, onSuccess, onFailure, disableNotification }) => {
    try {
      // Step 1: Create the asset images
      const response = await bulkCreateImages({
        variables: { ...variables, assetId: asset?.id },
        onSuccess: async (result) => {
          // Step 2: Check if this asset has shipment items
          const hasShipmentItems = asset?.shipmentItems && asset.shipmentItems.length > 0;
          
          if (hasShipmentItems && result?.images) {
            const shipmentItemId = asset.shipmentItems[0].id;
            const newImages = result.images;
            const existingAttachments = asset?.attachments || [];

            try {
              // Step 3: Create attachments for newly uploaded images
              // Only create attachments for images that don't already have one
              const attachmentPromises = newImages.map(async (image) => {
                // Check if attachment already exists with this pair_id
                const attachmentExists = existingAttachments.some(
                  att => att.pairId && String(att.pairId) === String(image.id)
                );

                if (attachmentExists) {
                  return null;
                }

                // Create the shipment item attachment
                return addAttachmentShipmentItems({
                  variables: {
                    url: image.url,
                    shipmentItemId,
                    pairId: parseInt(image.id, 10),
                  },
                }).catch(error => {
                  // Silent fail - don't break image upload if attachment creation fails
                  return null;
                });
              });

              await Promise.all(attachmentPromises);
            } catch (error) {
              // Attachment sync failed, but images were created successfully
              // Silent fail - don't break the entire operation
            }
          }

          // Step 4: Call the original onSuccess callback
          if (onSuccess) {
            onSuccess(result);
          }
        },
        onFailure,
        disableNotification,
      });

      return response;
    } catch (error) {
      if (onFailure) {
        onFailure(error);
      }
      throw error;
    }
  };

  return handleBulkCreateImages;
}
